// Общие элементы форм входа: поле пароля с кнопкой «показать», сообщение об ошибке, список правил пароля.
import { useId, useState, type ReactNode } from 'react';
import { Icon } from '../../ui/icons';

export function TextField({ label, value, onChange, testid, autoComplete, autoFocus, hint, readOnly, placeholder, maxLength }: {
  label: string; value: string; onChange: (v: string) => void; testid?: string; autoComplete?: string; autoFocus?: boolean;
  hint?: ReactNode; readOnly?: boolean; placeholder?: string; maxLength?: number;
}) {
  const id = useId();
  return (
    <div className="au-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type="text" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} autoComplete={autoComplete}
        autoFocus={autoFocus} readOnly={readOnly} placeholder={placeholder} maxLength={maxLength || 100} spellCheck={false}
        autoCapitalize="none" />
      {hint && <div className="au-hint">{hint}</div>}
    </div>
  );
}

export function PasswordField({ label, value, onChange, testid, autoComplete, autoFocus, hint }: {
  label: string; value: string; onChange: (v: string) => void; testid?: string; autoComplete?: string; autoFocus?: boolean; hint?: ReactNode;
}) {
  const id = useId();
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  return (
    <div className="au-field">
      <label htmlFor={id}>{label}</label>
      <div className="au-pass">
        <input id={id} type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          autoComplete={autoComplete} autoFocus={autoFocus} maxLength={300} spellCheck={false}
          onKeyUp={(e) => setCaps(e.getModifierState?.('CapsLock') || false)} />
        <button type="button" className="au-eye" onClick={() => setShow(!show)} aria-label={show ? 'Скрыть пароль' : 'Показать пароль'}
          aria-pressed={show} title={show ? 'Скрыть пароль' : 'Показать пароль'} data-testid={testid ? testid + '-show' : undefined} tabIndex={-1}>
          <Icon name={show ? 'eye-off' : 'eye'} size={16} />
        </button>
      </div>
      {caps && <div className="au-hint au-hint-warn"><Icon name="warn" size={12} /> Включён Caps Lock</div>}
      {hint && <div className="au-hint">{hint}</div>}
    </div>
  );
}

export function ErrorBox({ text, kind = 'error', testid = 'login-error' }: { text: string; kind?: 'error' | 'wait' | 'info'; testid?: string }) {
  if (!text) return null;
  return (
    <div className={'au-msg au-msg-' + kind} role="alert" data-testid={testid}>
      <Icon name={kind === 'wait' ? 'lock' : kind === 'info' ? 'info' : 'error'} size={16} />
      <span>{text}</span>
    </div>
  );
}

export function Check({ checked, onChange, label, testid, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; testid?: string; hint?: string }) {
  return (
    <label className="au-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      <span>{label}{hint && <span className="au-check-hint">{hint}</span>}</span>
    </label>
  );
}

/** Проверки пароля на лету (сервер всё равно проверяет сам, в том числе список популярных паролей). */
export function passwordChecks(pw: string, pw2: string, login: string, min = 8) {
  return [
    { ok: pw.length >= min, text: `Не короче ${min} символов` },
    { ok: !!pw && pw.trim().toLowerCase() !== login.trim().toLowerCase(), text: 'Не совпадает с логином' },
    { ok: !!pw && !/^(.)\1+$/.test(pw) && !/^(0123456789|1234567890|12345678|123456789|qwertyui|password|пароль)/i.test(pw), text: 'Не простой (не «12345678», не «password»)' },
    { ok: !!pw2 && pw === pw2, text: 'Пароли совпадают' },
  ];
}

export function Rules({ items }: { items: { ok: boolean; text: string }[] }) {
  return (
    <ul className="au-rules" aria-label="Требования к паролю">
      {items.map((r) => (
        <li key={r.text} className={r.ok ? 'is-ok' : ''}><Icon name={r.ok ? 'ok' : 'dot'} size={12} />{r.text}</li>
      ))}
    </ul>
  );
}

/** Ответ сервера об ошибке входа -> текст и вид сообщения (429 - пауза). */
export function errorOf(e: any): { text: string; kind: 'error' | 'wait' } {
  const st = e?.status;
  return { text: String(e?.message || e || 'Ошибка'), kind: st === 429 ? 'wait' : 'error' };
}

export const LOGIN_RE = /^[A-Za-z0-9._-]{3,32}$/;
export const LOGIN_HINT = 'Латинские буквы, цифры, точка, дефис, подчёркивание; 3–32 символа';
